import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Search,
  Visibility,
  Refresh,
  People,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  PersonOff,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  UserStatus,
  ReviewStatus,
  SilverConnectAdminUserListDto,
  CursorPageDto,
  AdminApiResponse,
} from '../../types';
import { apiClient } from '../../config/api';

// ─── Design tokens ────────────────────────────────────────────────
const PINK  = '#EC4899';
const CORAL = '#FF6B35';

// ─── Status configs ───────────────────────────────────────────────
const REVIEW_STATUS_CONFIG: Record<ReviewStatus, { label: string; color: string; bg: string; dot: string }> = {
  [ReviewStatus.APPROVED]:  { label: '승인됨',  color: '#2E7D32', bg: '#E8F5E9', dot: '#43A047' },
  [ReviewStatus.SUBMITTED]: { label: '제출됨',  color: '#E65100', bg: '#FFF3E0', dot: '#FF6D00' },
  [ReviewStatus.UPDATED]:   { label: '수정됨',  color: '#1565C0', bg: '#E3F2FD', dot: '#1E88E5' },
  [ReviewStatus.REJECTED]:  { label: '거부됨',  color: '#C62828', bg: '#FFEBEE', dot: '#E53935' },
};

const USER_STATUS_CONFIG: Partial<Record<UserStatus, { label: string; color: string }>> = {
  [UserStatus.APPROVED]: { label: '활성',   color: '#2E7D32' },
  [UserStatus.PENDING]:  { label: '대기',   color: '#E65100' },
  [UserStatus.REJECTED]: { label: '거부',   color: '#C62828' },
  [UserStatus.GUEST]:    { label: '게스트', color: '#9E9E9E' },
};

// Avatar ring color by review status
const AVATAR_RING: Record<ReviewStatus, string> = {
  [ReviewStatus.APPROVED]:  '#43A047',
  [ReviewStatus.SUBMITTED]: '#FF6D00',
  [ReviewStatus.UPDATED]:   '#1E88E5',
  [ReviewStatus.REJECTED]:  '#E53935',
};

// Row accent by review status
const ROW_ACCENT: Record<ReviewStatus, { border: string; bg: string; hover: string }> = {
  [ReviewStatus.APPROVED]:  { border: 'transparent', bg: '#fff',     hover: '#FAF5FF' },
  [ReviewStatus.SUBMITTED]: { border: '#FF6D00',     bg: '#FFFBF5',  hover: '#FFF3E0' },
  [ReviewStatus.UPDATED]:   { border: '#1E88E5',     bg: '#F5FAFF',  hover: '#E3F2FD' },
  [ReviewStatus.REJECTED]:  { border: '#E53935',     bg: '#FFF8F8',  hover: '#FFEBEE' },
};

// ─── Helpers ──────────────────────────────────────────────────────
const formatDate = (dateString: string | null) => {
  if (!dateString) return '–';
  const d = new Date(dateString);
  return d.toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getInitials = (name: string | null) =>
  name ? name.charAt(0).toUpperCase() : '?';

// Avatar background palette (index by userId mod 6)
const AVATAR_COLORS = ['#1976D2', '#7B1FA2', '#00897B', '#F57C00', '#C62828', '#5C6BC0'];

// ─── Main component ───────────────────────────────────────────────
const AdminUsersPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  // Auth guard
  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') navigate('/');
  }, [user, isLoading, navigate]);

  // ── State ─────────────────────────────────────────────────────
  const [users, setUsers]               = useState<SilverConnectAdminUserListDto[]>([]);
  const [loading, setLoading]           = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReviewStatus | 'ALL'>('ALL');
  const [cursor, setCursor]             = useState<string | null>(null);
  const [hasNext, setHasNext]           = useState(false);
  const [nextCursor, setNextCursor]     = useState<string | null>(null);

  // ── API ───────────────────────────────────────────────────────
  const fetchUsers = useCallback(async (reset: boolean = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (searchKeyword.trim()) params.append('searchKeyword', searchKeyword.trim());
      if (!reset && cursor) params.append('cursor', cursor);
      params.append('size', '20');

      const data: AdminApiResponse<CursorPageDto<SilverConnectAdminUserListDto>> =
        await apiClient.get(`/api-admin/v1/gth-common/silver-connect/members/cursor?${params}`);

      if (data.resultType === 'SUCCESS' && data.result) {
        if (reset) setUsers(data.result.content);
        else       setUsers(prev => [...prev, ...data.result.content]);
        setHasNext(data.result.hasNext);
        setNextCursor(data.result.nextCursor);
      }
    } catch (error) {
      console.error('사용자 목록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchKeyword, cursor]);

  useEffect(() => { fetchUsers(true); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    if (nextCursor && !loading) {
      setCursor(nextCursor);
      fetchUsers(false);
    }
  };

  const handleSearch = () => { setCursor(null); fetchUsers(true); };

  const handleStatusChange = (newStatus: ReviewStatus | 'ALL') => {
    setStatusFilter(newStatus);
    setCursor(null);
  };

  if (isLoading || !user || user.role !== 'ADMIN') return null;

  // Quick counts for filter bar
  const counts = users.reduce(
    (acc, u) => {
      if (u.reviewStatus in acc) acc[u.reviewStatus]++;
      return acc;
    },
    { [ReviewStatus.APPROVED]: 0, [ReviewStatus.SUBMITTED]: 0, [ReviewStatus.REJECTED]: 0 } as Record<ReviewStatus, number>
  );

  return (
    <AdminLayout>
      <style>{`
        @keyframes rowIn {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes userPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(1.3); }
        }
      `}</style>

      <Box sx={{ p: { xs: 2, md: 4 }, background: '#F8F4F9', minHeight: '100vh' }}>

        {/* ── Page header ── */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ color: PINK, fontWeight: 700, letterSpacing: 3, fontSize: '0.7rem' }}>
            ADMIN · MEMBERS
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{
              fontWeight: 900, color: '#1A0A2E', mt: 0.5,
              background: `linear-gradient(135deg, #1A0A2E 30%, ${PINK})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              사용자 관리
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              background: '#fff', border: '1px solid #F0E8F5', borderRadius: '100px',
              px: 2, py: 0.75,
            }}>
              <People sx={{ fontSize: 16, color: PINK }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#555' }}>
                {users.length}명 조회
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 0.5 }}>
            회원 심사 상태 및 계정 정보 관리
          </Typography>
        </Box>

        {/* ── Search & filter bar ── */}
        <Card elevation={0} sx={{ mb: 3, borderRadius: '20px', border: '1px solid #F0E8F5', p: 2.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            {/* Search input */}
            <TextField
              size="small"
              placeholder="이름 또는 이메일 검색..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              sx={{
                flex: '1 1 220px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&.Mui-focused fieldset': { borderColor: PINK },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: '#BBC' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch} sx={{ color: PINK }}>
                      <KeyboardArrowDown sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Status filter */}
            <FormControl size="small" sx={{ flex: '0 0 160px' }}>
              <Select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value as ReviewStatus | 'ALL')}
                sx={{
                  borderRadius: '12px',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PINK },
                }}
              >
                <MenuItem value="ALL">전체 보기</MenuItem>
                <MenuItem value={ReviewStatus.SUBMITTED}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#FF6D00' }} />
                    제출됨
                  </Box>
                </MenuItem>
                <MenuItem value={ReviewStatus.APPROVED}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#43A047' }} />
                    승인됨
                  </Box>
                </MenuItem>
                <MenuItem value={ReviewStatus.REJECTED}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#E53935' }} />
                    거부됨
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Search button */}
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                boxShadow: `0 4px 14px ${PINK}44`,
                '&:hover': { background: `linear-gradient(135deg, #D63384, #E55A28)` },
              }}
              startIcon={<Search sx={{ fontSize: 18 }} />}
            >
              검색
            </Button>

            {/* Refresh */}
            <Tooltip title="새로고침">
              <IconButton
                onClick={() => { setCursor(null); fetchUsers(true); }}
                disabled={loading}
                sx={{
                  border: '1px solid #F0E8F5',
                  borderRadius: '12px',
                  color: '#888',
                  '&:hover': { background: '#F8F4F9', color: PINK },
                }}
              >
                {loading
                  ? <CircularProgress size={18} sx={{ color: PINK }} />
                  : <Refresh sx={{ fontSize: 18 }} />
                }
              </IconButton>
            </Tooltip>
          </Box>

          {/* Quick stat pills */}
          {users.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
              {([
                { status: ReviewStatus.SUBMITTED, icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
                { status: ReviewStatus.APPROVED,  icon: <CheckCircle    sx={{ fontSize: 12 }} /> },
                { status: ReviewStatus.REJECTED,  icon: <Cancel         sx={{ fontSize: 12 }} /> },
              ] as { status: ReviewStatus; icon: React.ReactNode }[]).map(({ status, icon }) => {
                const cfg = REVIEW_STATUS_CONFIG[status];
                const count = counts[status];
                if (count === 0) return null;
                return (
                  <Box
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.7,
                      background: cfg.bg, color: cfg.color,
                      border: `1px solid ${cfg.dot}30`,
                      px: 1.5, py: 0.5, borderRadius: '100px',
                      fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                  >
                    {icon} {cfg.label} {count}명
                  </Box>
                );
              })}
            </Box>
          )}
        </Card>

        {/* ── Table ── */}
        <Card elevation={0} sx={{ borderRadius: '20px', border: '1px solid #F0E8F5', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(90deg, #1A0A2E 0%, #4A1265 100%)' }}>
                  {['프로필', '이름 / 이메일', '심사 상태', '계정 상태', '제출일', '최근 로그인', ''].map((h, i) => (
                    <TableCell
                      key={i}
                      align={i === 6 ? 'center' : 'left'}
                      sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 700, fontSize: '0.75rem', py: 1.8, whiteSpace: 'nowrap' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {users.map((adminUser, idx) => {
                  const reviewCfg = REVIEW_STATUS_CONFIG[adminUser.reviewStatus];
                  const userCfg   = adminUser.userStatus ? USER_STATUS_CONFIG[adminUser.userStatus] : null;
                  const accent    = ROW_ACCENT[adminUser.reviewStatus];
                  const avatarColor = AVATAR_COLORS[adminUser.userId % AVATAR_COLORS.length];

                  return (
                    <TableRow
                      key={adminUser.userId}
                      hover
                      onClick={() => navigate(`/admin/users/${adminUser.userId}`)}
                      sx={{
                        cursor: 'pointer',
                        borderLeft: `4px solid ${accent.border}`,
                        background: accent.bg,
                        '&:hover': { background: accent.hover },
                        transition: 'background 0.2s ease',
                        '& td': { borderBottom: '1px solid #F3EBF8' },
                        animation: `rowIn 0.3s ease ${idx * 0.03}s both`,
                      }}
                    >
                      {/* Avatar */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Avatar
                          src={adminUser.profileImageUrl || undefined}
                          alt={adminUser.name}
                          sx={{
                            width: 40, height: 40,
                            background: avatarColor,
                            fontSize: '0.9rem', fontWeight: 800,
                            outline: `2.5px solid ${reviewCfg.dot}`,
                            outlineOffset: 2,
                          }}
                        >
                          {getInitials(adminUser.name)}
                        </Avatar>
                      </TableCell>

                      {/* Name + Email */}
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, color: '#1A0A2E', fontSize: '0.88rem' }}>
                          {adminUser.name || '–'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#888', mt: 0.2 }}>
                          {adminUser.email}
                        </Typography>
                      </TableCell>

                      {/* 심사 상태 */}
                      <TableCell>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.6,
                          background: reviewCfg.bg,
                          color: reviewCfg.color,
                          border: `1px solid ${reviewCfg.dot}33`,
                          px: 1.2, py: 0.4,
                          borderRadius: '100px',
                          fontSize: '0.72rem', fontWeight: 700,
                        }}>
                          <Box sx={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: reviewCfg.dot,
                            ...(adminUser.reviewStatus === ReviewStatus.SUBMITTED && {
                              animation: 'userPulse 1.8s ease-in-out infinite',
                            }),
                          }} />
                          {reviewCfg.label}
                        </Box>
                      </TableCell>

                      {/* 계정 상태 */}
                      <TableCell>
                        {userCfg ? (
                          <Chip
                            label={userCfg.label}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: `${userCfg.color}44`,
                              color: userCfg.color,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 22,
                            }}
                          />
                        ) : (
                          <Typography sx={{ color: '#CCC', fontSize: '0.8rem' }}>–</Typography>
                        )}
                      </TableCell>

                      {/* 제출일 */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', color: '#666', whiteSpace: 'nowrap' }}>
                          {formatDate(adminUser.submittedTime)}
                        </Typography>
                      </TableCell>

                      {/* 최근 로그인 */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', color: '#888', whiteSpace: 'nowrap' }}>
                          {formatDate(adminUser.lastLoginTime)}
                        </Typography>
                      </TableCell>

                      {/* 액션 */}
                      <TableCell align="center">
                        <Tooltip title="상세 보기">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${adminUser.userId}`); }}
                            sx={{
                              color: '#9C27B0',
                              border: '1px solid #EDE0F5',
                              borderRadius: '10px',
                              '&:hover': { background: '#F3E5F5', borderColor: PINK },
                            }}
                          >
                            <Visibility sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {users.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <PersonOff sx={{ fontSize: 48, color: '#DDD', mb: 1 }} />
                      <Typography sx={{ color: '#AAA', fontWeight: 600 }}>사용자를 찾을 수 없습니다</Typography>
                      <Typography sx={{ color: '#CCC', fontSize: '0.8rem', mt: 0.5 }}>
                        검색 조건을 변경하거나 필터를 초기화해 주세요.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {loading && users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={32} sx={{ color: PINK }} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Load more */}
          {hasNext && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, borderTop: '1px solid #F3EBF8' }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} sx={{ color: PINK }} /> : <KeyboardArrowDown />}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: `${PINK}66`,
                  color: PINK,
                  px: 4,
                  '&:hover': { borderColor: PINK, background: `${PINK}08` },
                }}
              >
                {loading ? '로딩 중...' : '더 보기'}
              </Button>
            </Box>
          )}
        </Card>
      </Box>
    </AdminLayout>
  );
};

export default AdminUsersPage;
